import type { DialogueCue } from './story';

/**
 * Plays the single mastered film mix. Keeping one media clock avoids cue-boundary
 * swaps and repeated 80 ms hard seeks, both of which are audible on mobile when
 * WebGL rendering briefly occupies the main thread.
 */
export class MasterAudioPlayer {
  private readonly audio = new Audio('/audio/final-mix.m4a');
  private muted = false;
  private unlocked = false;
  private warnedPlayback = false;
  private lastFilmTime = 0;
  private lastPlaying = false;
  private forceSeek = true;

  initialize(): void {
    this.audio.preload = 'auto';
    this.audio.setAttribute('playsinline', '');
    this.audio.load();
    const unlock = () => {
      void this.unlock();
      document.removeEventListener('pointerdown', unlock, true);
      document.removeEventListener('keydown', unlock, true);
    };
    document.addEventListener('pointerdown', unlock, true);
    document.addEventListener('keydown', unlock, true);
  }

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    const desiredMuted = this.muted;
    this.audio.muted = true;
    try {
      await this.audio.play();
      this.audio.pause();
      this.unlocked = true;
    } catch {
      // A later direct click on Play can still satisfy media autoplay policy.
    } finally {
      this.audio.muted = desiredMuted;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.audio.muted = muted;
  }

  seek(seconds: number): void {
    this.lastFilmTime = seconds;
    this.forceSeek = true;
    this.safeSeek(seconds);
  }

  pause(): void {
    this.lastPlaying = false;
    this.audio.playbackRate = 1;
    if (!this.audio.paused) this.audio.pause();
  }

  sync(filmTime: number, playing: boolean): void {
    const timelineJump = Math.abs(filmTime - this.lastFilmTime) > 0.35;
    const playbackChanged = playing !== this.lastPlaying;
    this.lastFilmTime = filmTime;
    this.lastPlaying = playing;

    if (!playing) {
      this.pause();
      return;
    }
    const drift = filmTime - this.audio.currentTime;
    if (this.forceSeek || timelineJump || playbackChanged || Math.abs(drift) > 0.7) {
      this.safeSeek(filmTime);
      this.forceSeek = false;
      this.audio.playbackRate = 1;
    } else if (Math.abs(drift) > 0.12) {
      // Imperceptible rate correction handles normal render jitter without a
      // discontinuous media seek. Clamp tightly to protect voice performance.
      this.audio.playbackRate = THREEClamp(1 + drift * 0.035, 0.985, 1.015);
    } else {
      this.audio.playbackRate = 1;
    }
    if (this.audio.paused) this.safePlay();
  }

  dispose(): void {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.audio.load();
  }

  private safeSeek(seconds: number): void {
    if (this.audio.readyState < HTMLMediaElement.HAVE_METADATA) {
      this.audio.addEventListener('loadedmetadata', () => this.safeSeek(seconds), { once: true });
      return;
    }
    const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 490;
    try { this.audio.currentTime = THREEClamp(seconds, 0, Math.max(0, duration - 0.01)); } catch { /* rapid source setup */ }
  }

  private safePlay(): void {
    void this.audio.play().catch((error: unknown) => {
      if (!this.warnedPlayback) {
        this.warnedPlayback = true;
        console.warn('Film audio was blocked. Tap Play once to enable sound.', error);
      }
    });
  }
}

function THREEClamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export class AmbientScore {
  private context?: AudioContext;
  private master?: GainNode;
  private wind?: GainNode;
  private drone?: GainNode;
  private tones: OscillatorNode[] = [];
  private started = false;

  async start(): Promise<void> {
    if (!this.context) this.build();
    if (!this.context) return;
    await this.context.resume();
    this.started = true;
    this.master?.gain.setTargetAtTime(0.2, this.context.currentTime, 1.3);
  }

  setMuted(muted: boolean): void {
    if (!this.context || !this.master) return;
    this.master.gain.setTargetAtTime(muted ? 0 : 0.2, this.context.currentTime, 0.18);
  }

  update(seconds: number, chapterIndex: number): void {
    if (!this.started || !this.context || !this.drone || !this.wind) return;
    const t = this.context.currentTime;
    const roots = [55, 49, 46.25, 41.2, 43.65, 58.27, 65.41];
    const root = roots[chapterIndex] ?? 55;
    this.tones[0]?.frequency.setTargetAtTime(root, t, 2.5);
    this.tones[1]?.frequency.setTargetAtTime(root * 1.5, t, 2.5);
    this.tones[2]?.frequency.setTargetAtTime(root * 2.01, t, 2.5);
    const silenceDip = seconds > 263 && seconds < 338 ? 0.035 : 0.11;
    const dawnRise = chapterIndex >= 5 ? 0.16 : silenceDip;
    this.drone.gain.setTargetAtTime(dawnRise, t, 1.7);
    this.wind.gain.setTargetAtTime(chapterIndex === 3 ? 0.17 : chapterIndex === 4 ? 0.055 : 0.095, t, 1.2);
  }

  dispose(): void {
    for (const tone of this.tones) tone.stop();
    void this.context?.close();
  }

  private build(): void {
    const AudioContextCtor = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioContextCtor) return;
    const context = new AudioContextCtor();
    const master = context.createGain();
    master.gain.value = 0;
    master.connect(context.destination);

    const drone = context.createGain();
    drone.gain.value = 0.08;
    const droneFilter = context.createBiquadFilter();
    droneFilter.type = 'lowpass';
    droneFilter.frequency.value = 420;
    droneFilter.Q.value = 0.7;
    drone.connect(droneFilter).connect(master);

    const toneSpecs: Array<[OscillatorType, number, number]> = [
      ['sine', 55, 0.8],
      ['sine', 82.5, 0.22],
      ['triangle', 110.5, 0.055],
    ];
    for (const [type, frequency, level] of toneSpecs) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = type;
      oscillator.frequency.value = frequency;
      gain.gain.value = level;
      oscillator.connect(gain).connect(drone);
      oscillator.start();
      this.tones.push(oscillator);
    }

    const noiseBuffer = context.createBuffer(1, context.sampleRate * 4, context.sampleRate);
    const noise = noiseBuffer.getChannelData(0);
    let previous = 0;
    for (let i = 0; i < noise.length; i += 1) {
      const white = Math.random() * 2 - 1;
      previous = previous * 0.985 + white * 0.015;
      noise[i] = previous * 3.2;
    }
    const windSource = context.createBufferSource();
    windSource.buffer = noiseBuffer;
    windSource.loop = true;
    const windFilter = context.createBiquadFilter();
    windFilter.type = 'bandpass';
    windFilter.frequency.value = 520;
    windFilter.Q.value = 0.35;
    const wind = context.createGain();
    wind.gain.value = 0.08;
    windSource.connect(windFilter).connect(wind).connect(master);
    windSource.start();

    this.context = context;
    this.master = master;
    this.drone = drone;
    this.wind = wind;
  }
}

export class DialoguePlayer {
  private readonly audio = new Audio();
  private readonly assets = new Map<string, DialogueAsset>();
  private readonly preloadTasks = new Map<string, Promise<string>>();
  private readonly blobUrls = new Set<string>();
  private activeId = '';
  private enabled = false;
  private muted = false;
  private generation = 0;
  private lastFilmTime = 0;
  private lastPlaying = false;
  private unlockArmed = false;
  private warnedPlayback = false;

  async initialize(): Promise<void> {
    this.armUnlock();
    try {
      const response = await fetch('./audio-state.json');
      if (!response.ok) throw new Error(`audio state ${response.status}`);
      const state = await response.json() as DialogueState;
      this.enabled = state.dialogueAvailable === true && state.mode === 'APPROVED_ELEVENLABS';
      if (this.enabled) {
        for (const asset of state.assets ?? []) this.assets.set(asset.cueId, asset);
        void this.preloadAll([...this.assets.values()]);
      }
    } catch {
      this.enabled = false;
    }
    this.audio.preload = 'auto';
    window.addEventListener('beforeunload', () => this.dispose(), { once: true });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.audio.muted = muted;
  }

  sync(cue: DialogueCue | undefined, filmTime: number, playing: boolean): void {
    this.lastFilmTime = filmTime;
    this.lastPlaying = playing;
    if (!this.enabled || !cue) {
      if (!this.audio.paused) this.audio.pause();
      this.generation += 1;
      this.activeId = '';
      return;
    }
    if (cue.id !== this.activeId) {
      this.activeId = cue.id;
      const generation = ++this.generation;
      void this.activateCue(cue, generation);
      return;
    }
    if (this.audio.readyState < HTMLMediaElement.HAVE_METADATA) return;
    const desired = this.desiredTime(cue, filmTime);
    const asset = this.assets.get(cue.id);
    if (asset && filmTime - cue.start > asset.spokenDuration) {
      if (!this.audio.paused) this.audio.pause();
      return;
    }
    if (Math.abs(this.audio.currentTime - desired) > 0.08) this.safeSeek(desired);
    if (playing && this.audio.paused) this.safePlay();
    if (!playing && !this.audio.paused) this.audio.pause();
  }

  private async activateCue(cue: DialogueCue, generation: number): Promise<void> {
    const asset = this.assets.get(cue.id);
    const source = asset ? await this.sourceFor(asset) : cue.audioFile;
    if (generation !== this.generation || this.activeId !== cue.id) return;
    this.audio.src = source;
    this.audio.muted = this.muted;
    this.audio.preload = 'auto';
    this.audio.load();
    try {
      await new Promise<void>((resolve, reject) => {
        if (this.audio.readyState >= HTMLMediaElement.HAVE_METADATA) return resolve();
        const loaded = () => { cleanup(); resolve(); };
        const failed = () => { cleanup(); reject(new Error(`Unable to decode ${cue.id}`)); };
        const cleanup = () => {
          this.audio.removeEventListener('loadedmetadata', loaded);
          this.audio.removeEventListener('error', failed);
        };
        this.audio.addEventListener('loadedmetadata', loaded, { once: true });
        this.audio.addEventListener('error', failed, { once: true });
      });
    } catch (error) {
      console.warn('Dialogue audio unavailable.', error);
      return;
    }
    if (generation !== this.generation || this.activeId !== cue.id) return;
    this.safeSeek(this.desiredTime(cue, this.lastFilmTime));
    if (this.lastPlaying) this.safePlay();
  }

  private desiredTime(cue: DialogueCue, filmTime: number): number {
    const asset = this.assets.get(cue.id);
    const desired = Math.max(0, filmTime - cue.start) + (asset?.trimStart ?? 0);
    const upper = Number.isFinite(this.audio.duration) ? Math.max(0, this.audio.duration - 0.01) : desired;
    return Math.min(desired, upper);
  }

  private safeSeek(seconds: number): void {
    try { this.audio.currentTime = Math.max(0, seconds); } catch { /* metadata can be invalidated during a rapid seek */ }
  }

  private safePlay(): void {
    void this.audio.play().catch((error: unknown) => {
      if (!this.warnedPlayback) {
        this.warnedPlayback = true;
        console.warn('Dialogue playback was blocked. Use the play control once to unlock audio.', error);
      }
    });
  }

  private armUnlock(): void {
    if (this.unlockArmed) return;
    this.unlockArmed = true;
    const unlock = () => {
      const silent = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=');
      silent.volume = 0.01;
      void silent.play().then(() => { silent.pause(); }).catch(() => undefined);
      document.removeEventListener('pointerdown', unlock, true);
      document.removeEventListener('keydown', unlock, true);
    };
    document.addEventListener('pointerdown', unlock, true);
    document.addEventListener('keydown', unlock, true);
  }

  private async preloadAll(assets: DialogueAsset[]): Promise<void> {
    const queue = [...assets];
    const worker = async () => {
      while (queue.length) {
        const asset = queue.shift();
        if (asset) await this.sourceFor(asset);
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, queue.length) }, () => worker()));
  }

  private sourceFor(asset: DialogueAsset): Promise<string> {
    const existing = this.preloadTasks.get(asset.cueId);
    if (existing) return existing;
    const task = fetch(asset.audioFile)
      .then((response) => {
        if (!response.ok) throw new Error(`${asset.audioFile}: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        this.blobUrls.add(url);
        return url;
      })
      .catch((error) => {
        console.warn('Dialogue preload failed; falling back to direct media loading.', error);
        return asset.audioFile;
      });
    this.preloadTasks.set(asset.cueId, task);
    return task;
  }

  private dispose(): void {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.audio.load();
    for (const url of this.blobUrls) URL.revokeObjectURL(url);
    this.blobUrls.clear();
  }
}

interface DialogueAsset {
  cueId: string;
  audioFile: string;
  alignmentFile: string;
  trimStart: number;
  spokenDuration: number;
  takeHash: string;
}

interface DialogueState {
  dialogueAvailable?: boolean;
  mode?: string;
  assets?: DialogueAsset[];
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
